import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  ModalContent,
  ModalFooter
} from '@clientbase/clientbase-library'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { retrySyncFailure } from 'api/contaAzul'
import { ContaAzulInfo } from 'models/AccountsPayable'

import Can from 'components/Can'
import ContaAzulStatusChip from 'components/ContaAzulStatusChip'

import Item from '../../Item'

const CA_FINANCEIRO_URL =
  'https://pro.contaazul.com/#/ca/financeiro/contas-a-pagar'

interface ContaAzulSectionProps {
  info: ContaAzulInfo
  onRefresh?: () => void
}

const ContaAzulSection: React.FC<ContaAzulSectionProps> = ({
  info,
  onRefresh
}) => {
  const [copied, setCopied] = useState(false)
  const [retrying, setRetrying] = useState(false)

  const handleCopy = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true)
        toast.success('Copiado.')
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => toast.error('Falha ao copiar.'))
  }

  const handleOpenCa = () => {
    window.open(CA_FINANCEIRO_URL, '_blank', 'noopener,noreferrer')
  }

  const handleRetry = async () => {
    try {
      setRetrying(true)
      await retrySyncFailure(info.uuid)
      toast.success('Sincronização re-enfileirada.')
      onRefresh?.()
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao re-enfileirar.')
    } finally {
      setRetrying(false)
    }
  }

  // Identificador "mais resolvido" disponível — mostra o mais avançado do ciclo.
  const identifier =
    info.caInstallmentId || info.caProtocolId || info.caExternalId

  return (
    <>
      <ModalContent>
        <Box>
          <Item
            textKey="Status"
            value={<ContaAzulStatusChip status={info.status} />}
          />

          {identifier && (
            <Item
              textKey="Identificador no CA"
              value={
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <span>{identifier}</span>
                  <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(identifier)}
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
          )}

          {info.status === 'error' && info.lastError && (
            <Item textKey="Erro" value={info.lastError} />
          )}
        </Box>
      </ModalContent>

      <ModalFooter>
        <Stack direction="row" justifyContent="flex-end" gap={1} width="100%">
          {/*
            Esta secao e compartilhada pelo detalhe de despesa e pelo de cobranca, e o
            "Tentar novamente" chama retrySyncFailure -> POST conta_azul/sync_failures/:uuid/retry,
            que o backend protege com `integrations:manage` (o controller e generico:
            atende Billing, AccountsPayable e OtherRevenue, entao nao da para amarra-lo
            a permissao de um modulo so). Por isso o gate aqui NAO e a permissao do
            modulo dono do registro — e a de integracoes mesmo.
          */}
          {info.status === 'error' && (
            <Can permission="integrations:manage">
              <Button
                variantButton="outlined"
                onClick={handleRetry}
                loading={retrying}
                disabled={retrying}
                startIcon="faRepeat"
              >
                Tentar novamente
              </Button>
            </Can>
          )}
          <Button
            variantButton="outlined"
            onClick={handleOpenCa}
            endIcon="open"
          >
            Abrir Conta Azul
          </Button>
        </Stack>
      </ModalFooter>
    </>
  )
}

export default ContaAzulSection
